// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the output window (docs/AYNI_XR_MULTI_GLOBE_PLAN.md, phase 4): a
 * second browser window that shows the globes and nothing else, for the
 * projector in a lecture hall, a dome or a wall, while the controls stay
 * on the laptop. It follows this page the way a room's follower follows
 * its lead, over a BroadcastChannel instead of the room server: the same
 * state (dataset, playhead, view, layers, the globes), the same sync.
 *
 * `createOutputSocket` plays a WebSocket for `roomClient.connectRoom`:
 * the main window is told it leads, the output window that it follows,
 * and every message the lead sends crosses the channel. A page in a
 * meeting opens its output window on the meeting instead (`?room=`), so
 * the room does the relaying and the output shows what everyone sees.
 */

export const OUTPUT_PARAM = 'output'

/** The channel an output window follows, from its address; null when this is not one. */
export function outputChannelFromSearch(search: string = typeof window === 'undefined' ? '' : window.location.search): string | null {
  const v = new URLSearchParams(search).get(OUTPUT_PARAM)?.trim() ?? ''
  return /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v : null
}

/** AYNI (multi-globe plan, phase 5): an output window that is a sphere feed carries its width (`sphere=2048|4096`). */
export const SPHERE_PARAM = 'sphere'

/** The sphere feed's width this output window was opened for, or null when it shows globes. */
export function sphereFeedFromSearch(search: string = typeof window === 'undefined' ? '' : window.location.search): '2048' | '4096' | null {
  const v = new URLSearchParams(search).get(SPHERE_PARAM)
  return v === '2048' || v === '4096' ? v : null
}

/** Whether this page is an output window: it shows globes only. */
export function isOutputWindow(search: string = typeof window === 'undefined' ? '' : window.location.search): boolean {
  return outputChannelFromSearch(search) !== null
}

/** A fresh channel id. */
export function newOutputChannel(random: () => number = Math.random): string {
  return `out-${Math.floor(random() * 36 ** 8).toString(36).padStart(8, '0')}`
}

/**
 * The address an output window opens: this page's dataset and globes, plus
 * `output=<channel>`; in a meeting, the meeting's room (the output joins
 * it as one more follower) and `output=room`.
 */
export function outputWindowUrl(base: string, channel: string, room: string | null, sphere: '2048' | '4096' | null = null): string {
  const url = new URL(base)
  if (sphere) url.searchParams.set(SPHERE_PARAM, sphere)
  else url.searchParams.delete(SPHERE_PARAM)
  url.searchParams.delete('st')
  url.searchParams.delete('signin')
  url.searchParams.delete('meetingKey')
  if (room) {
    url.searchParams.set('room', room)
    url.searchParams.set(OUTPUT_PARAM, 'room')
  } else {
    url.searchParams.delete('room')
    url.searchParams.set(OUTPUT_PARAM, channel)
  }
  return url.toString()
}

type Listener = (ev: { data?: string }) => void

/** The least of a WebSocket that `roomClient` uses. */
export interface OutputSocket {
  readyState: number
  addEventListener(type: 'open' | 'message' | 'close' | 'error', listener: Listener): void
  send(text: string): void
  close(): void
}

interface Channel {
  postMessage(data: unknown): void
  close(): void
  onmessage: ((ev: { data: unknown }) => void) | null
}

/**
 * A socket over a BroadcastChannel. `role: 'main'` is the window that
 * leads: the room welcomes it as the lead, and what it sends goes to the
 * channel. `role: 'output'` follows: welcomed as a follower of 'main', it
 * receives the lead's states from the channel (and the latest one at once,
 * which the lead repeats within a second anyway). Nothing else crosses.
 */
export function createOutputSocket(
  role: 'main' | 'output',
  channelId: string,
  open: (name: string) => Channel = (name) => new BroadcastChannel(name) as unknown as Channel,
): OutputSocket {
  const listeners: Record<string, Listener[]> = { open: [], message: [], close: [], error: [] }
  const channel = open(`ayni-output-${channelId}`)
  const me = role === 'main' ? 'main' : 'output'
  const emit = (type: string, data?: string) => {
    for (const fn of listeners[type] ?? []) fn(data === undefined ? {} : { data })
  }
  const socket: OutputSocket = {
    readyState: 0,
    addEventListener(type, listener) {
      listeners[type].push(listener)
    },
    send(text) {
      // Only the lead speaks, and only states are worth the channel.
      if (role !== 'main' || socket.readyState !== 1) return
      try {
        const msg = JSON.parse(text) as { t?: string }
        if (msg.t === 'state') channel.postMessage(text)
      } catch {
        // Not a message of ours.
      }
    },
    close() {
      if (socket.readyState === 3) return
      socket.readyState = 3
      channel.onmessage = null
      channel.close()
      emit('close')
    },
  }
  channel.onmessage = (ev) => {
    if (role !== 'output' || typeof ev.data !== 'string') return
    emit('message', ev.data)
  }
  // Opened on the next turn, as a real socket is: listeners are on by then.
  queueMicrotask(() => {
    if (socket.readyState === 3) return
    socket.readyState = 1
    emit('open')
    emit('message', JSON.stringify({
      t: 'welcome',
      you: me,
      lead: 'main',
      count: 2,
      state: null,
      meeting: false,
      seat: null,
      media: null,
      ticket: null,
      activity: null,
      results: null,
      spotlight: null,
      grants: null,
      drivers: [],
      driven: [],
    }))
  })
  return socket
}
